import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const Home = () => {
  return (
    <div className="bg-white">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-gray-50 to-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-5xl md:text-6xl font-bold mb-6">
            <span className="text-teal-600">Form</span>
            <span className="text-teal-800">Forge</span>
            <span className="text-teal-600">Pro</span>
          </div>
          <p className="text-xl text-gray-600 mb-4">
            Your AI-powered legal assistant for trusts, wills, incorporations, and legal guidance
          </p>
          <div className="flex justify-center gap-4 text-sm mb-8">
            <Badge className="bg-teal-100 text-teal-800">
              Enhanced with Real Legal Data
            </Badge>
            <Badge className="bg-teal-100 text-teal-800">
              Free Government APIs
            </Badge>
            <Badge className="bg-teal-100 text-teal-800">
              AI Document Analysis
            </Badge>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              asChild
              size="lg"
              className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold px-8 py-3"
            >
              <Link to="/services">Get Started Free</Link>
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className="border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-3"
            >
              <Link to="/demo">See Demo</Link>
            </Button>
          </div>

          {/* Stats */}
          <div className="flex justify-center gap-8 mt-12 text-center">
            <div>
              <div className="text-2xl font-bold text-teal-700">70K+</div>
              <div className="text-sm text-gray-600">Documents Created</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-teal-700">99.9%</div>
              <div className="text-sm text-gray-600">Accuracy Rate</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-teal-700">$4M+</div>
              <div className="text-sm text-gray-600">Legal Fees Saved</div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-5xl mx-auto">
            <div className="flex items-center space-x-3 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="flex-shrink-0 w-2 h-2 bg-teal-500 rounded-full"></div>
              <span className="text-gray-800 font-medium">Lawyer-vetted templates</span>
            </div>
            <div className="flex items-center space-x-3 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="flex-shrink-0 w-2 h-2 bg-teal-500 rounded-full"></div>
              <span className="text-gray-800 font-medium">Instant PDF generation</span>
            </div>
            <div className="flex items-center space-x-3 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="flex-shrink-0 w-2 h-2 bg-teal-500 rounded-full"></div>
              <span className="text-gray-800 font-medium">Secure & Encrypted</span>
            </div>
            <div className="flex items-center space-x-3 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="flex-shrink-0 w-2 h-2 bg-teal-500 rounded-full"></div>
              <span className="text-gray-800 font-medium">Zero hidden fees</span>
            </div>
            <div className="flex items-center space-x-3 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="flex-shrink-0 w-2 h-2 bg-teal-500 rounded-full"></div>
              <span className="text-gray-800 font-medium">Available nationwide</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Comprehensive Legal Services
            </h2>
            <p className="text-xl text-gray-600">
              From patents to business formation, our AI handles all your legal document needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-xl text-gray-800">
                  Business & IP Protection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Patents, trademarks, LLC formation, and business contracts
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Patent Applications</li>
                  <li>• Trademark Registration</li>
                  <li>• LLC Formation</li>
                  <li>• Business Contracts</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-xl text-gray-800">
                  Estate Planning
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Wills, trusts, and comprehensive estate planning documents
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Simple & Complex Wills</li>
                  <li>• Living Trusts</li>
                  <li>• Power of Attorney</li>
                  <li>• Healthcare Directives</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-xl text-gray-800">
                  Family & Personal
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Divorce, custody, and personal legal documents
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Uncontested Divorce</li>
                  <li>• Child Custody</li>
                  <li>• Name Changes</li>
                  <li>• Personal Contracts</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button 
              asChild
              size="lg"
              className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold px-8 py-3"
            >
              <Link to="/services">View All Services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-600">
            <strong>Legal Disclaimer:</strong> FormForgePro provides informational guidance only, not formal legal representation or advice. 
            For decisions specific to your situation, consult a licensed attorney.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;